"use client";
import { useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BrainCircuit, Chrome, ExternalLink } from "lucide-react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import type { ComponentType } from "react";

type Step = {
  id: string;
  image: string;
  alt: string;
  aspectClass?: string;
  minHeightClass?: string;
  fit?: "cover" | "contain";
  frameClass?: string;
  unoptimized?: boolean;
  wrapperClass?: string;
};

type TabId = "chrome-extension" | "manual-add";

type TabContent = {
  id: TabId;
  label: string;
  title: string;
  description: string;
  steps: Step[];
  cta?: {
    text: string;
    href: string;
    icon?: ComponentType<{ className?: string }>;
  };
};

const MANUAL_ADD_STEPS: Step[] = [
  {
    id: "step-1",
    image:
      "https://gnxrddsvzynszstsraer.supabase.co/storage/v1/object/public/Tavlo/plus_d9xheg.gif",
    alt: "Animated preview of saving content into Tavlo.",
    aspectClass: "aspect-[16/9]",
    minHeightClass: "min-h-[240px] md:min-h-[320px]",
    fit: "contain",
    frameClass: "p-4 md:p-6 bg-surface-solid",
    unoptimized: true,
    wrapperClass: "lg:mt-20 xl:mt-48",
  },
  {
    id: "step-2",
    image:
      "https://gnxrddsvzynszstsraer.supabase.co/storage/v1/object/public/Tavlo/step_2_bitli9.gif",
    alt: "Saved content organized in Tavlo with filters and tags.",
    aspectClass: "aspect-[3/4] md:aspect-[4/5]",
    minHeightClass: "min-h-[400px] md:min-h-[600px]",
    fit: "cover",
  },
  {
    id: "step-3",
    image:
      "https://gnxrddsvzynszstsraer.supabase.co/storage/v1/object/public/Tavlo/Screenshot_2025-12-26_at_6.21.43_AM_bpul29.png",
    alt: "Tavlo summary view showing curated saves and highlights.",
    aspectClass: "aspect-[3/4] md:aspect-[4/5]",
    minHeightClass: "min-h-[400px] md:min-h-[600px]",
    fit: "contain",
    frameClass: "p-4 md:p-6 bg-surface-solid",
  },
];

const TAB_CONTENT: Record<TabId, TabContent> = {
  "chrome-extension": {
    id: "chrome-extension",
    label: "Chrome Extension",
    title: "Build your second brain in two simple steps",
    description:
      "Install the Tavlo Chrome extension and save any link instantly with a single click. No copy-pasting required.",
    steps: [
      {
        id: "ext-step-1",
        image: "/extension1.png",
        alt: "Install Tavlo Chrome extension from Chrome Web Store",
        aspectClass: "aspect-[16/9]",
        minHeightClass: "min-h-[240px] md:min-h-[320px]",
        fit: "cover",
        wrapperClass: "lg:mt-20 xl:mt-48",
      },
      {
        id: "ext-step-2",
        image: "/extension2.png",
        alt: "Click Tavlo extension icon to save any link instantly",
        aspectClass: "aspect-[14/9]",
        minHeightClass: "min-h-[240px] md:min-h-[320px]",
        fit: "contain",
        wrapperClass: "lg:mb-20 xl:mb-48",
      },
    ],
    cta: {
      text: "Get Chrome Extension",
      href: "https://chromewebstore.google.com/detail/tavlo-save-to-your-feed/enjgpnmebahcbmkgnndjbmgceocelgpa",
      icon: Chrome,
    },
  },
  "manual-add": {
    id: "manual-add",
    label: "Manual Add",
    title: "Build your second brain in three simple steps",
    description:
      "Once logged in, click the `Add` button on the navigation menu. Paste the link to your desired content and click `Save & process`. That's it! After the post is done processing, it will be available for viewing in your inbox and library",
    steps: MANUAL_ADD_STEPS,
  },
};

// Helper component to render step content
const StepsContent = ({ content }: { content: TabContent }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 relative items-start">
      {/* LEFT COLUMN - STICKY TEXT CONTENT */}
      <div className="lg:sticky lg:top-24 lg:h-[calc(100vh-12rem)] flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-1/10 text-brand-1 text-sm font-semibold mb-6 w-fit"
        >
          <BrainCircuit className="w-4 h-4" />
          <span>HOW IT WORKS</span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-text-primary mb-6 leading-tight"
        >
          {content.title.split("**").map((part, i) =>
            i % 2 === 0 ? (
              <span key={i}>{part}</span>
            ) : (
              <span
                key={i}
                className="text-transparent bg-clip-text bg-gradient-to-r from-brand-1 to-brand-2"
              >
                {part}
              </span>
            ),
          )}
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="text-lg md:text-xl text-text-secondary leading-relaxed max-w-xl"
        >
          {content.description}
        </motion.p>

        {/* CTA Button (if provided) */}
        {content.cta && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-8"
          >
            <Button
              asChild
              size="lg"
              className="gap-2 bg-gradient-to-r from-brand-1 to-brand-2 text-white border-0 hover:shadow-lg hover:shadow-brand-1/20 rounded-full w-full sm:w-auto"
            >
              <a
                href={content.cta.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${content.cta.text} (opens in new tab)`}
              >
                {content.cta.icon && <content.cta.icon className="w-4 h-4" />}
                {content.cta.text}
                <ExternalLink className="w-4 h-4" />
              </a>
            </Button>
          </motion.div>
        )}
      </div>

      {/* RIGHT COLUMN - SCROLLING IMAGES */}
      <div className="flex flex-col gap-12 md:gap-24">
        {content.steps.map((step, index) => (
          <div
            key={step.id}
            className={cn(
              "relative w-full",
              step.minHeightClass ?? "min-h-[400px] md:min-h-[600px]",
              step.wrapperClass,
            )}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: false, amount: 0.5 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className={cn(
                "relative w-full h-full rounded-2xl overflow-hidden shadow-2xl border border-border-light bg-surface-solid",
                step.aspectClass ?? "aspect-[3/4] md:aspect-[4/5]",
                step.frameClass,
              )}
            >
              <div className="relative w-full h-full">
                <Image
                  src={step.image}
                  alt={step.alt || `Tavlo workflow step ${index + 1}`}
                  fill
                  sizes="(max-width: 1024px) 100vw, 520px"
                  className={
                    step.fit === "contain" ? "object-contain" : "object-cover"
                  }
                  quality={95}
                  unoptimized={step.unoptimized}
                />
              </div>
            </motion.div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ScrollingFeatures = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <section
      id="how-it-works"
      className="relative w-full bg-bg-page py-12 md:py-24"
      ref={containerRef}
    >
      <div className="container mx-auto px-4 md:px-6">
        <Tabs defaultValue="chrome-extension" className="w-full">
          {/* Tab Switcher */}
          <div className="flex justify-center mb-8 md:mb-12">
            <TabsList className="inline-flex h-10 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground max-w-md">
              <TabsTrigger
                value="chrome-extension"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all"
              >
                <Chrome className="w-4 h-4" />
                Chrome Extension
              </TabsTrigger>
              <TabsTrigger
                value="manual-add"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all"
              >
                Manual Add
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Chrome Extension Content */}
          <TabsContent value="chrome-extension" className="mt-0">
            <AnimatePresence mode="wait">
              <motion.div
                key="chrome-extension"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <StepsContent content={TAB_CONTENT["chrome-extension"]} />
              </motion.div>
            </AnimatePresence>
          </TabsContent>

          {/* Manual Add Content */}
          <TabsContent value="manual-add" className="mt-0">
            <AnimatePresence mode="wait">
              <motion.div
                key="manual-add"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <StepsContent content={TAB_CONTENT["manual-add"]} />
              </motion.div>
            </AnimatePresence>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default ScrollingFeatures;
